import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useStream, EStreamProtocol } from '@/shared/streaming';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import { resolveAssistantVariant, type TAssistantVariant } from '../constants';
import { parseInsightSections } from '../components/connection-diagnostics-utils';

/** Greeting i18n key per assistant flow. */
const GREETING_KEYS: Record<TAssistantVariant, string> = {
  connect: 'integrations.assistant.panel.greeting',
  diagnosis: 'integrations.assistant.panel.diagnosisGreeting',
  discover: 'integrations.assistant.panel.discoveryGreeting',
  trigger: 'integrations.assistant.panel.triggerGreeting',
};

interface IAssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  quickActions?: string[];
}

interface IUseIntegrationAssistantReturn {
  messages: IAssistantMessage[];
  sendMessage: (text: string) => void;
  isLoading: boolean;
  streamingContent: string;
  quickActions: string[];
}

/** A Rain typed value, as delivered over the thread SSE stream. */
interface IRainValue {
  string_value?: string;
}

/** Rain event shape (only the fields we care about). */
interface IRainEvent {
  id?: string;
  output_text?: { text: string };
  // `is_remote` marks a client-executed (custom_remote) tool: generation pauses
  // and the browser must run it and POST the result back. `name`/`index` let us
  // match the tool and correlate its argument deltas.
  tool_call_start?: {
    id?: string;
    name?: string;
    index?: number;
    is_remote?: boolean;
  };
  // Argument deltas for a tool call, correlated by `index` to the start event.
  tool_call_argument?: { index?: number; name?: string; value?: IRainValue };
  generation_complete?: { usage: unknown; model: string; id: string };
  generation_failed?: { message: string; kind?: string };
  follow_up?: { suggestion: string };
}

/**
 * Client tool shifu calls to autocomplete the trigger-setup form. Declared as a
 * `custom_remote` source on the shifu agent; the agent pauses when it calls
 * this, and we fulfill it here by writing the value into the form and POSTing
 * the result back so generation resumes.
 */
const SET_TRIGGER_FIELD_TOOL = 'set_trigger_field';

/**
 * Client tool shifu calls to add a filter condition to the trigger (the
 * dialog's "Filters" section: run only when an event matches a condition).
 */
const ADD_TRIGGER_FILTER_TOOL = 'add_trigger_filter';

/** Client tool to remove one filter condition from the trigger. */
const REMOVE_TRIGGER_FILTER_TOOL = 'remove_trigger_filter';

/** Client tool to remove all filter conditions from the trigger. */
const CLEAR_TRIGGER_FILTERS_TOOL = 'clear_trigger_filters';

/** Names of the client tools we fulfill in the browser. */
const CLIENT_TOOL_NAMES = [
  SET_TRIGGER_FIELD_TOOL,
  ADD_TRIGGER_FILTER_TOOL,
  REMOVE_TRIGGER_FILTER_TOOL,
  CLEAR_TRIGGER_FILTERS_TOOL,
];

// Required argument keys per client tool, so we can fulfil a call as soon as its
// last argument lands (instead of relying on the debounce window) and reject a
// call that settles with arguments missing rather than acting on partial input.
const CLIENT_TOOL_REQUIRED_ARGS: Record<string, readonly string[]> = {
  [SET_TRIGGER_FIELD_TOOL]: ['field', 'value'],
  // `operator` must be required: if it arrives after `value` (or the stream
  // orders args differently from the declared params), fulfilling on
  // field+value alone would default a non-equals filter to `equals` and report
  // success with the wrong operator.
  [ADD_TRIGGER_FILTER_TOOL]: ['field', 'operator', 'value'],
  [REMOVE_TRIGGER_FILTER_TOOL]: ['field', 'operator', 'value'],
  [CLEAR_TRIGGER_FILTERS_TOOL]: [],
};

/** Whether every required argument for a client tool call has arrived. */
function clientToolArgsComplete(
  name: string,
  args: Record<string, string>,
): boolean {
  const required = CLIENT_TOOL_REQUIRED_ARGS[name] ?? [];
  return required.every(key => key in args);
}

// How long to wait after the last argument delta of a remote tool call before
// fulfilling it. This is only a fallback: a call is normally fulfilled the
// moment its last required argument lands (see clientToolArgsComplete), so slow,
// >150ms-apart deltas no longer cause fulfilment with partial arguments.
const REMOTE_TOOL_SETTLE_MS = 150;

// After we swallow a tool-pass `generation_complete` (betting a final completion
// follows), how long the stream may stay silent before we clear the loader
// anyway. Guards against a missing final completion leaving the chat stuck on
// the loader forever; any further stream event cancels it, so a legitimately
// slow tool pass never trips it.
const LOADER_WATCHDOG_MS = 45000;

// Hidden message sent on open in trigger-setup mode to make shifu produce its
// proactive opener (list the fields, offer to autocomplete). Not shown in the
// chat — the agent's reply is the first visible message.
const TRIGGER_OPENING_KICK = 'Open the trigger setup — start here.';

// Active agents — the new var-* pair has the parent-path-routed thread variables
// (organizations, workspaces, books, versions, authentications) wired up. The
// old IDs below are the previously-used pair, kept as comments so we can roll
// back by swapping these two lines if the migration runs into trouble.
const RAIN_AGENT_ID = 'shifu';
// const RAIN_AGENT_ID = 'integrations-connection-assistant';

const RAIN_BASE = '/api/v1';

const MAX_QUICK_ACTIONS = 3;

/**
 * Builds the parent-path-routed agent base URL. The four base segments map to
 * the agent's thread variables (`organizations`, `workspaces`, `books`,
 * `versions`), which Rain auto-injects into the thread on creation. The
 * org/workspace prefix is also load-bearing for the role-management middleware
 * in front of the API gateway, so it must be present on every request.
 *
 * When `selectedAuthId` is provided, an `authentications/{id}` pair is inserted
 * before `/agents/...`. Rain's parent-path parser maps that to the optional
 * `selected_authentication_id` thread variable, scoping the agent's responses
 * to that single authentication method.
 */
function rainParentPath(
  organizationId: string,
  workspaceId: string,
  bookName: string,
  bookVersion: string,
  selectedAuthId: string | undefined,
  connectionId: string | undefined,
): string {
  const seg = (s: string) => encodeURIComponent(s);
  const authSegment = selectedAuthId
    ? `/authentications/${seg(selectedAuthId)}`
    : '';
  // Present only in the Edit Connection flow: routes the agent into
  // connection-diagnosis mode for this specific saved connection. In practice
  // selectedAuthId and connectionId are mutually exclusive (connect vs edit).
  const connectionSegment = connectionId
    ? `/connections/${seg(connectionId)}`
    : '';
  return (
    `${RAIN_BASE}` +
    `/organizations/${seg(organizationId)}` +
    `/workspaces/${seg(workspaceId)}` +
    `/books/${seg(bookName)}` +
    `/versions/${seg(bookVersion)}` +
    authSegment +
    connectionSegment
  );
}

/**
 * Fire-and-forget SendMessage. Adds the user message to the thread and kicks
 * off generation asynchronously. Events arrive via the thread's SSE stream.
 */
async function rainSendMessage(
  agentBase: string,
  agentId: string,
  threadId: string,
  content: string,
): Promise<void> {
  await axios.post(
    `${agentBase}/threads/${threadId}/input`,
    {
      agent_id: agentId,
      thread_id: threadId,
      message: { content },
    },
    { headers: { 'Content-Type': 'application/json' } },
  );
}

/**
 * Post the result of a client-executed (remote) tool call back to Rain, which
 * un-pauses the agent's generation. Routed through `agentBase` so the gateway
 * sees the same tenant parent-path as the stream/input calls.
 */
async function rainSendToolResult(
  agentBase: string,
  threadId: string,
  toolCallId: string,
  success: boolean,
  content?: IRainValue,
): Promise<void> {
  await axios.post(
    `${agentBase}/threads/${threadId}/tool-results`,
    {
      tool_call_id: toolCallId,
      content: content ?? { string_value: success ? 'ok' : '' },
      success,
    },
    { headers: { 'Content-Type': 'application/json' } },
  );
}

/**
 * Source id for the client-tool capability shifu uses to fill the form. The
 * `client.` prefix is the convention Rain expects for client-declared
 * capabilities (so re-registering is idempotent and addressable).
 */
const CLIENT_TOOL_SOURCE_ID = 'client.triggerform';

/** Display name of the `custom_remote` source that exposes the client tools. */
const CLIENT_TOOL_SOURCE_NAME = 'Trigger form';

/** One client tool's declaration, as sent in the `custom_remote` source config. */
interface IClientToolConfig {
  name: string;
  description: string;
  status_template: string;
  parameters: {
    name: string;
    description: string;
    value_type: { kind: { type: string } };
  }[];
}

/**
 * Per-tool `custom_remote` declarations, keyed by tool name. Only the tools
 * whose host callbacks were supplied are registered (see `rainRegisterClientTools`),
 * so e.g. a poll — which wires only `onFillForm` — never advertises the filter
 * tools to shifu.
 */
const CLIENT_TOOL_CONFIGS: Record<string, IClientToolConfig> = {
  [SET_TRIGGER_FIELD_TOOL]: {
    name: SET_TRIGGER_FIELD_TOOL,
    description:
      'Fill ONE field of the trigger-setup form in the UI with a value the ' +
      'user has confirmed. Call once per field as soon as the value is ' +
      'settled (e.g. the repository to watch); the browser writes it into ' +
      'that form input and returns immediately.',
    status_template: 'Filling ${field}',
    parameters: [
      {
        name: 'field',
        description:
          'The configuration field key to set, exactly as it appears in the ' +
          'trigger\'s configuration (e.g. "repository").',
        value_type: { kind: { type: 'String' } },
      },
      {
        name: 'value',
        description: 'The value to put in that field (e.g. "kognitos/bdk").',
        value_type: { kind: { type: 'String' } },
      },
    ],
  },
  [ADD_TRIGGER_FILTER_TOOL]: {
    name: ADD_TRIGGER_FILTER_TOOL,
    description:
      'Add ONE filter condition so the trigger only fires when an event ' +
      "matches it. Use the trigger's event fields (not its configuration " +
      'fields). Call once per condition.',
    status_template: 'Adding filter on ${field}',
    parameters: [
      {
        name: 'field',
        description:
          'The event field to filter on (e.g. "action", or a nested field ' +
          'like "pull_request.state"), taken from the trigger\'s event shape.',
        value_type: { kind: { type: 'String' } },
      },
      {
        name: 'operator',
        description: 'One of: equals, not_equals, contains, not_contains.',
        value_type: { kind: { type: 'String' } },
      },
      {
        name: 'value',
        description: 'The value to compare against (e.g. "opened").',
        value_type: { kind: { type: 'String' } },
      },
    ],
  },
  [REMOVE_TRIGGER_FILTER_TOOL]: {
    name: REMOVE_TRIGGER_FILTER_TOOL,
    description:
      'Remove ONE filter condition. Pass the same field, operator, and ' +
      'value that identify the condition to remove.',
    status_template: 'Removing filter on ${field}',
    parameters: [
      {
        name: 'field',
        description: 'The event field of the condition to remove.',
        value_type: { kind: { type: 'String' } },
      },
      {
        name: 'operator',
        description: 'equals, not_equals, contains, or not_contains.',
        value_type: { kind: { type: 'String' } },
      },
      {
        name: 'value',
        description: 'The value of the condition to remove.',
        value_type: { kind: { type: 'String' } },
      },
    ],
  },
  [CLEAR_TRIGGER_FILTERS_TOOL]: {
    name: CLEAR_TRIGGER_FILTERS_TOOL,
    description:
      'Remove ALL filter conditions from the trigger (reset the Filters ' +
      'section). Takes no arguments.',
    status_template: 'Clearing filters',
    parameters: [],
  },
};

/**
 * Register the `set_trigger_field` client tool on a freshly-created thread so
 * shifu can call it and have the browser fulfill it. This is the "client
 * capability" pattern: a `custom_remote` tool source is created per-thread and
 * its tools enabled, after which the agent's calls pause awaiting our result.
 * (A manifest-declared `custom_remote` source does NOT mark the call remote, so
 * it must be registered here at runtime.) Best-effort: on failure the tool just
 * won't be available and shifu falls back to advising the value in text.
 *
 * Only the tools named in `toolNames` are advertised, so shifu is never told
 * about a capability the host didn't wire (e.g. a poll, which has no filters,
 * registers just `set_trigger_field`).
 */
async function rainRegisterClientTools(
  agentBase: string,
  threadId: string,
  toolNames: readonly string[],
): Promise<void> {
  // Nothing to advertise (no host callbacks were supplied): skip registration
  // entirely so an empty source isn't created.
  if (toolNames.length === 0) return;
  // A short timeout so a mis-routed/hung call fails fast instead of leaving the
  // registration (and any dependent retry) pending forever.
  const opts = {
    headers: { 'Content-Type': 'application/json' },
    timeout: 10000,
  };
  // 1. Create the custom_remote source with only the requested tools.
  await axios.post(
    `${agentBase}/threads/${threadId}/tool-sources`,
    {
      type: 'custom_remote',
      source_id: CLIENT_TOOL_SOURCE_ID,
      config: {
        name: CLIENT_TOOL_SOURCE_NAME,
        tools: toolNames.map(name => CLIENT_TOOL_CONFIGS[name]),
      },
    },
    opts,
  );
  // 2. Read back the tool ids the source produced (server-generated).
  const listResponse = await axios.get<{
    tools?: { id?: string; source_id?: string }[];
  }>(`${agentBase}/threads/${threadId}/tools`, { timeout: 10000 });
  const toolIds = (listResponse.data?.tools ?? [])
    .filter(tool => tool.source_id === CLIENT_TOOL_SOURCE_ID && tool.id)
    .map(tool => tool.id as string);
  if (toolIds.length === 0) return;
  // 3. Enable them so shifu is actually offered the tool.
  await axios.post(
    `${agentBase}/threads/${threadId}/tools/${encodeURIComponent(
      CLIENT_TOOL_SOURCE_ID,
    )}:enable`,
    { tool_ids: toolIds },
    opts,
  );
}

/**
 * Create a thread under the parent-path-routed agent base. Built manually
 * (rather than via the generated `createThread` hook) because the generated
 * client hard-codes the `/api/v1` prefix; combining that with the parent-path
 * `baseURL` would double the prefix. Building off `agentBase` keeps a single
 * `/api/v1` and matches the gateway HTTPRoute, consistent with the SSE and
 * SendMessage URLs.
 */
async function rainCreateThread(
  agentBase: string,
  title: string,
  variables?: Record<string, string>,
): Promise<string | null> {
  const response = await axios.post<{ thread_id?: string }>(
    `${agentBase}/threads`,
    // `variables` fills thread-scoped agent variables that aren't part of the
    // parent-path (e.g. `mode`). Only send variables the agent declares — Rain
    // rejects unknown ones with a 400.
    variables ? { title, variables } : { title },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return response.data?.thread_id ?? null;
}

/** Delete a thread under the parent-path-routed agent base. */
async function rainDeleteThread(
  agentBase: string,
  threadId: string,
): Promise<void> {
  await axios.delete(`${agentBase}/threads/${threadId}`);
}

/**
 * Fire-and-forget thread deletion. Swallows errors because the call is purely
 * cleanup (closing dialog, switching auth step, unmount) — failing to delete
 * is annoying but never user-visible, so we don't want unhandled rejections.
 */
function rainDeleteThreadBestEffort(agentBase: string, threadId: string): void {
  rainDeleteThread(agentBase, threadId).catch(() => {});
}

/**
 * The agent never saw the diagnosis shown as the opening message (it came from
 * a separate thread), so the first turn carries it as context.
 */
function buildDiagnosisContext(
  { cause, fixes }: { cause: string; fixes: string },
  question: string,
): string {
  return [
    'Context: you already diagnosed why this connection is failing, and the user has seen this explanation:',
    cause,
    fixes && `Potential fixes:\n${fixes}`,
    "Answer the user's question below directly. Don't repeat the diagnosis unless they ask.",
    `User question: ${question}`,
  ]
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Trigger-setup options for the assistant, supplied only in `mode: 'trigger'`.
 * The callbacks let shifu drive the host dialog's form through its client tools;
 * grouping them keeps the hook's positional parameters from ballooning.
 */
export interface ITriggerAssistantOptions {
  /** The trigger (poll or webhook) being configured, so the agent reads its params. */
  triggerId?: string;
  /**
   * Called when shifu autocompletes a form field via the `set_trigger_field`
   * client tool. Lets the host dialog write the value into its form. Returns an
   * error message if the field isn't a fillable config key (so shifu can
   * correct), or nothing on success.
   */
  onFillForm?: (field: string, value: string) => string | void;
  /**
   * Called when shifu adds a filter via `add_trigger_filter`. Returns an error
   * message if the field/operator is invalid (so shifu can correct), else nothing.
   */
  onAddFilter?: (
    field: string,
    operator: string,
    value: string,
  ) => string | void;
  /**
   * Called when shifu removes a filter via `remove_trigger_filter`. Returns an
   * error message if no matching filter was found.
   */
  onRemoveFilter?: (
    field: string,
    operator: string,
    value: string,
  ) => string | void;
  /** Called when shifu clears all filters via `clear_trigger_filters`. */
  onClearFilters?: () => void;
}

/**
 * Hook for managing the integration assistant chat.
 * Uses Rain agentic service via the API gateway with auth.
 *
 * The four book/tenant identifiers go into the URL via parent-path routing,
 * which both feeds Rain's thread-variable resolver and satisfies the role-
 * management middleware. The user message body is the raw user text — the
 * agent fetches the book's auth shape itself via the BDK RetrieveBook tool.
 *
 * Sends via SendMessage (POST /input, fire-and-forget) and consumes the
 * thread's SSE stream for all events (output_text, generation_complete,
 * follow_up suggestions). Mirrors the rain UI's architecture.
 */
export function useIntegrationAssistant(
  bookName: string,
  integrationDisplayName: string,
  bookVersion: string,
  organizationId: string,
  workspaceId: string,
  selectedAuthId: string | undefined,
  isOpen: boolean,
  connectionId?: string,
  mode?: 'discover' | 'trigger',
  diagnosis?: string,
  triggerOptions?: ITriggerAssistantOptions,
): IUseIntegrationAssistantReturn {
  const { t } = useTranslation('integrations');
  const { triggerId, onFillForm, onAddFilter, onRemoveFilter, onClearFilters } =
    triggerOptions ?? {};
  // Greeting depends on the flow: discovery (Configure Actions) vs diagnosis
  // (Edit Connection) vs trigger setup (Poll/Webhook Trigger dialog) vs the
  // default connect/setup flow. Derived from the same helper the panel uses so
  // the greeting, starters, and the `mode` thread variable can't diverge.
  const variant = resolveAssistantVariant(connectionId, mode);
  const greetingKey = GREETING_KEYS[variant];
  // Trigger setup leads with a proactive, agent-generated opener (it lists the
  // trigger's fields and offers to autocomplete), instead of a static greeting.
  // So we seed no greeting, show the loader immediately, and kick the agent to
  // produce the opener once the thread is ready.
  const usesProactiveOpening = variant === 'trigger';
  const idCounter = useRef(0);

  const diagnosisTurn = useMemo(() => {
    if (variant !== 'diagnosis' || !diagnosis) return undefined;
    const sections = parseInsightSections(diagnosis);
    const greeting = [
      sections.cause,
      sections.fixes &&
        `**${t('integrations.connectionDiagnostics.fixesTitle')}**\n\n${sections.fixes}`,
      t('integrations.assistant.panel.diagnosisFollowUp'),
    ]
      .filter(Boolean)
      .join('\n\n');
    return { sections, greeting };
  }, [variant, diagnosis, t]);
  const diagnosisTurnRef = useRef(diagnosisTurn);
  const hasSentRef = useRef(false);

  const generateId = () => {
    idCounter.current += 1;
    return `msg-${idCounter.current}`;
  };

  const initialMessages = (): IAssistantMessage[] =>
    usesProactiveOpening
      ? []
      : [{ id: generateId(), role: 'assistant', content: t(greetingKey) }];

  const { getAccessTokenSilently } = useAuth();

  const [messages, setMessages] =
    useState<IAssistantMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(usesProactiveOpening);
  const [streamingContent, setStreamingContent] = useState('');
  const [quickActions, setQuickActions] = useState<string[]>([]);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Rain thread state
  const [threadId, setThreadId] = useState<string | null>(null);
  // Mirrored at render (not in an effect) because the init/cleanup effects below
  // read it synchronously to tear down the previous thread; an effect-based sync
  // would run too late for those reads.
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = threadId;

  // Per-turn streaming buffer driven by SSE events.
  const turnTextRef = useRef('');
  // Dedupe persisted events on SSE replay (ephemeral events have no id).
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  // True between a `tool_call_start` and the intermediate `generation_complete`
  // that closes shifu's tool-call pass, so we can tell that completion apart
  // from the real end-of-turn one (see handleRainEvent).
  const toolCallPendingRef = useRef(false);
  // Safety timer armed when we swallow a tool-pass completion; fires only if the
  // stream then goes silent (missing final completion) so the loader can't hang.
  const loaderWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // In-flight client-executed (remote) tool calls, keyed by tool_call_id, plus
  // an index→id map so argument deltas (which carry only the index) can find
  // their call. Each entry accumulates args and debounces fulfillment.
  const remoteCallsRef = useRef<
    Map<
      string,
      {
        name: string;
        args: Record<string, string>;
        timer: ReturnType<typeof setTimeout> | null;
      }
    >
  >(new Map());
  const toolCallIndexToIdRef = useRef<Map<number, string>>(new Map());
  // Kept in refs so the stable SSE handler can reach the latest values without
  // re-subscribing the stream on every render. Synced in an effect below.
  const onFillFormRef = useRef(onFillForm);
  const onAddFilterRef = useRef(onAddFilter);
  const onRemoveFilterRef = useRef(onRemoveFilter);
  const onClearFiltersRef = useRef(onClearFilters);

  // Generated rain hooks (createThread/deleteThread) hit the proto-declared
  // `/agents/{id}/threads` path. The org/workspace/book/version parent-path
  // segments live outside the proto and are passed via axios `baseURL` so the
  // gateway's role-management middleware sees the right tenant prefix.
  const parentPath = rainParentPath(
    organizationId,
    workspaceId,
    bookName,
    bookVersion,
    selectedAuthId,
    connectionId,
  );
  const agentBase = `${parentPath}/agents/${RAIN_AGENT_ID}`;
  // Ref mirror so the stable SSE handler can POST tool results to the current
  // agent base without taking it as a dependency. Synced in the effect below.
  const agentBaseRef = useRef(agentBase);
  // Tracks the parent path used to *create* the current thread, so the unmount
  // cleanup and the re-init effect can DELETE under the same prefix the thread
  // was created with — important after a selection change rebuilds parentPath.
  const parentPathRef = useRef(parentPath);

  // Mirror the latest callbacks and derived values into refs so the stable SSE
  // handler and the debounced tool-call fulfillment (both async) read current
  // values without re-subscribing the stream. Done in an effect rather than
  // during render to stay React Compiler-safe; these are only read from async
  // code that runs after commit, so an effect is timely enough.
  // eslint-disable-next-line no-restricted-syntax -- reason: syncing imperative refs consumed by the async SSE handler / tool-call fulfillment
  useEffect(() => {
    onFillFormRef.current = onFillForm;
    onAddFilterRef.current = onAddFilter;
    onRemoveFilterRef.current = onRemoveFilter;
    onClearFiltersRef.current = onClearFilters;
    agentBaseRef.current = agentBase;
    diagnosisTurnRef.current = diagnosisTurn;
  });

  // Create / re-create the Rain thread whenever isOpen flips on or agentBase
  // changes (e.g. the user picked an auth method, so selectedAuthId — and thus
  // the parent-path-routed URL — changed). Tearing the old thread down resets
  // chat history and triggers fresh suggestions, matching the old per-step UX.
  // eslint-disable-next-line no-restricted-syntax -- reason: thread lifecycle requires imperative setup
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const previousThreadId = threadIdRef.current;
    const previousParentPath = parentPathRef.current;
    setThreadId(null); // sendMessage bails until the new thread lands
    parentPathRef.current = parentPath;
    turnTextRef.current = '';
    seenEventIdsRef.current = new Set();
    toolCallPendingRef.current = false;
    hasSentRef.current = false;
    if (loaderWatchdogRef.current) {
      clearTimeout(loaderWatchdogRef.current);
      loaderWatchdogRef.current = null;
    }
    // Drop any in-flight client tool calls from the previous thread.
    remoteCallsRef.current.forEach(c => c.timer && clearTimeout(c.timer));
    remoteCallsRef.current.clear();
    toolCallIndexToIdRef.current.clear();

    // Tear down the previous thread under the parent path it was created with —
    // important: delete must use that same prefix, not the new one.
    if (previousThreadId) {
      rainDeleteThreadBestEffort(
        `${previousParentPath}/agents/${RAIN_AGENT_ID}`,
        previousThreadId,
      );
    }

    // Reset chat on every (re-)init: a static greeting for most flows, or an
    // empty thread + loader for trigger setup (the agent produces the opener).
    setMessages(
      usesProactiveOpening
        ? []
        : [{ id: generateId(), role: 'assistant', content: t(greetingKey) }],
    );
    setStreamingContent('');
    setQuickActions([]);
    setShowQuickActions(false);
    // Clear any in-flight generation from the torn-down thread, then show the
    // loader again if this flow opens with an agent-generated opener.
    setIsLoading(usesProactiveOpening);

    const initThread = async () => {
      try {
        const newThreadId = await rainCreateThread(
          `${parentPath}/agents/${RAIN_AGENT_ID}`,
          `Integration assistant - ${integrationDisplayName || bookName}`,
          // The connection is in scope (parent-path); the flow is selected by
          // the `mode` thread variable, sent in the body. Poll-setup also pins
          // which trigger via `trigger_id` so the agent reads the right one's
          // parameters (poll or webhook).
          mode
            ? { mode, ...(triggerId ? { trigger_id: triggerId } : {}) }
            : undefined,
        );

        if (cancelled) return;

        // Expose the thread (starts the stream, enables input) BEFORE anything
        // else, so the chat always works even if tool registration is slow or
        // fails.
        setThreadId(newThreadId);

        // Register the form-fill client tools for this thread so shifu can call
        // them. Only trigger-setup mode has a form to fill. Awaited BEFORE the
        // opening kick so the tools are enabled before the agent's first turn;
        // otherwise it could start generating before they exist. Best-effort: a
        // failure is logged and swallowed so it never blocks or breaks the chat
        // (the assistant just falls back to advising values in text). The chat /
        // input already work because setThreadId ran first.
        if (mode === 'trigger' && newThreadId) {
          // Advertise only the tools whose host callbacks were supplied, so a
          // poll (onFillForm only) never exposes the filter tools to shifu.
          const enabledTools: string[] = [];
          if (onFillFormRef.current) enabledTools.push(SET_TRIGGER_FIELD_TOOL);
          if (onAddFilterRef.current)
            enabledTools.push(ADD_TRIGGER_FILTER_TOOL);
          if (onRemoveFilterRef.current)
            enabledTools.push(REMOVE_TRIGGER_FILTER_TOOL);
          if (onClearFiltersRef.current)
            enabledTools.push(CLEAR_TRIGGER_FILTERS_TOOL);
          try {
            await rainRegisterClientTools(
              `${parentPath}/agents/${RAIN_AGENT_ID}`,
              newThreadId,
              enabledTools,
            );
          } catch (error) {
            console.warn('Failed to register client tools:', error);
          }
          if (cancelled) return;
        }

        // Kick the agent to produce its proactive opener (hidden message, not
        // shown in the chat). The loader is already up; it clears when the
        // opener's generation_complete arrives.
        if (usesProactiveOpening && newThreadId) {
          rainSendMessage(
            `${parentPath}/agents/${RAIN_AGENT_ID}`,
            RAIN_AGENT_ID,
            newThreadId,
            TRIGGER_OPENING_KICK,
          ).catch(error => {
            console.warn('Failed to kick trigger opener:', error);
            if (!cancelled) setIsLoading(false);
          });
        }
      } catch (error) {
        console.warn('Failed to initialize Rain thread:', error);
        if (!cancelled) setIsLoading(false);
      }
    };

    initThread();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    parentPath,
    bookName,
    integrationDisplayName,
    greetingKey,
    mode,
    triggerId,
    usesProactiveOpening,
    t,
  ]);

  // Delete Rain thread on unmount
  // eslint-disable-next-line no-restricted-syntax -- reason: cleanup on unmount
  useEffect(() => {
    const remoteCalls = remoteCallsRef.current;
    return () => {
      remoteCalls.forEach(c => c.timer && clearTimeout(c.timer));
      remoteCalls.clear();
      if (loaderWatchdogRef.current) clearTimeout(loaderWatchdogRef.current);
      if (threadIdRef.current) {
        rainDeleteThreadBestEffort(
          `${parentPathRef.current}/agents/${RAIN_AGENT_ID}`,
          threadIdRef.current,
        );
      }
    };
  }, []);

  // Execute a settled client tool call and post the result so generation
  // resumes. Reads everything from refs, so it is stable across renders.
  const fulfillRemoteCall = useCallback((toolCallId: string) => {
    const call = remoteCallsRef.current.get(toolCallId);
    if (!call) return;
    remoteCallsRef.current.delete(toolCallId);
    for (const [idx, mappedId] of toolCallIndexToIdRef.current.entries()) {
      if (mappedId === toolCallId) toolCallIndexToIdRef.current.delete(idx);
    }

    let success = false;
    let resultText = '';
    // Guard against fulfilling on partial arguments (the fallback debounce may
    // fire before a slow argument stream finishes): reject rather than act on an
    // incomplete call, so shifu retries with every argument instead of writing
    // an empty value and being told it succeeded.
    const incomplete = !clientToolArgsComplete(call.name, call.args);
    if (call.name === SET_TRIGGER_FIELD_TOOL) {
      const field = call.args.field;
      if (!onFillFormRef.current) {
        resultText = 'This form is read-only; fields cannot be filled.';
      } else if (incomplete) {
        resultText =
          'Call set_trigger_field again with both a field and a value.';
      } else {
        // The host returns an error string if the field isn't a fillable config
        // key, so shifu can correct; undefined means the value was written.
        const error = onFillFormRef.current(field, call.args.value);
        if (error) {
          resultText = error;
        } else {
          success = true;
          resultText = `Set ${field} to ${call.args.value}`;
        }
      }
    } else if (call.name === ADD_TRIGGER_FILTER_TOOL) {
      const field = call.args.field;
      const operator = call.args.operator;
      const value = call.args.value;
      if (!onAddFilterRef.current) {
        resultText = 'Filters are not available for this trigger.';
      } else if (incomplete) {
        resultText =
          'Call add_trigger_filter again with a field, operator, and value.';
      } else {
        // The host returns an error string if the field/operator is invalid,
        // so shifu can correct; undefined means the filter was added.
        const error = onAddFilterRef.current(field, operator, value);
        if (error) {
          resultText = error;
        } else {
          success = true;
          resultText = `Added filter: ${field} ${operator} ${value}`;
        }
      }
    } else if (call.name === REMOVE_TRIGGER_FILTER_TOOL) {
      const field = call.args.field;
      const operator = call.args.operator;
      const value = call.args.value;
      if (!onRemoveFilterRef.current) {
        resultText = 'Filters are not available for this trigger.';
      } else if (incomplete) {
        resultText =
          'Call remove_trigger_filter again with a field, operator, and value.';
      } else {
        const error = onRemoveFilterRef.current(field, operator, value);
        if (error) {
          resultText = error;
        } else {
          success = true;
          resultText = `Removed filter: ${field} ${operator} ${value}`;
        }
      }
    } else if (call.name === CLEAR_TRIGGER_FILTERS_TOOL) {
      if (!onClearFiltersRef.current) {
        resultText = 'Filters are not available for this trigger.';
      } else {
        onClearFiltersRef.current();
        success = true;
        resultText = 'Cleared all filters.';
      }
    } else {
      resultText = `Unknown client tool: ${call.name}`;
    }

    const threadId = threadIdRef.current;
    if (threadId) {
      // Posting the result resumes the paused turn — shifu streams its reply
      // next. The turn's generation_complete already fired (it arrives before
      // the remote tool_call_start), so isLoading is false; re-show the loader
      // and lock the input until the resumed turn completes, otherwise a message
      // sent now would interleave with shifu's reply.
      setIsLoading(true);
      rainSendToolResult(agentBaseRef.current, threadId, toolCallId, success, {
        string_value: resultText,
      }).catch(error =>
        console.warn('Failed to send tool result to Rain:', error),
      );
    }
  }, []);

  // Stable handler for SSE events.
  const handleRainEvent = useCallback(
    (event: IRainEvent) => {
      // Dedupe persisted events on replay. Ephemeral events (follow_up,
      // streamed output_text chunks pre-completion) have no `id` and always
      // pass through.
      if (event.id) {
        if (seenEventIdsRef.current.has(event.id)) return;
        seenEventIdsRef.current.add(event.id);
      }

      // Any incoming event means the stream is alive, so cancel a pending
      // loader watchdog (it's only meant to fire during true silence).
      if (loaderWatchdogRef.current) {
        clearTimeout(loaderWatchdogRef.current);
        loaderWatchdogRef.current = null;
      }

      if (event.output_text) {
        turnTextRef.current += event.output_text.text;
        setStreamingContent(turnTextRef.current);
        return;
      }

      // shifu answers by first calling a tool (`get_connection`,
      // `retrieve_book`). Rain emits a `generation_complete` at the end of that
      // first pass — BEFORE the tool result comes back and the real answer
      // streams. Remember that a tool call is in flight so we don't mistake
      // that intermediate completion for the end of the turn (see below).
      if (event.tool_call_start) {
        const tc = event.tool_call_start;
        // Client-executed (custom_remote) tool: generation pauses until we POST
        // a result. We recognise it by name — the stream omits `is_remote`/
        // `index` when they are the proto defaults (false/0), so we can't gate
        // on `is_remote`, and a missing index means 0. The remote ToolCallStart
        // arrives *after* the turn's generation_complete.
        if (tc.id && tc.name && CLIENT_TOOL_NAMES.includes(tc.name)) {
          const toolCallId = tc.id;
          toolCallIndexToIdRef.current.set(tc.index ?? 0, toolCallId);
          // A no-argument call (e.g. clear_trigger_filters) emits no
          // tool_call_argument events, so arm the settle timer now or the result
          // would never be POSTed and generation would hang. Calls that DO take
          // arguments must NOT arm it here: a first argument delta arriving more
          // than REMOTE_TOOL_SETTLE_MS after the start would otherwise fulfil
          // with empty args. For those, the argument handler arms/reschedules
          // the timer (and fulfils immediately once every required arg lands).
          const takesNoArgs =
            (CLIENT_TOOL_REQUIRED_ARGS[tc.name] ?? []).length === 0;
          remoteCallsRef.current.set(toolCallId, {
            name: tc.name,
            args: {},
            timer: takesNoArgs
              ? setTimeout(
                  () => fulfillRemoteCall(toolCallId),
                  REMOTE_TOOL_SETTLE_MS,
                )
              : null,
          });
          return;
        }
        // Server-side tool pass (e.g. get_connection / RetrieveBook): shifu
        // emits a pre-answer generation_complete right after; flag it so that
        // completion isn't mistaken for end-of-turn (see below).
        toolCallPendingRef.current = true;
        return;
      }

      if (event.tool_call_argument) {
        const arg = event.tool_call_argument;
        // A missing index is the proto default, 0 — same convention as the
        // start event above.
        const id = toolCallIndexToIdRef.current.get(arg.index ?? 0);
        if (!id) return;
        const call = remoteCallsRef.current.get(id);
        if (!call) return;
        if (arg.name) {
          call.args[arg.name] = arg.value?.string_value ?? '';
        }
        // Fulfil as soon as every required argument has landed, so a slow stream
        // (deltas >150ms apart) can't fire the debounce with partial arguments.
        if (clientToolArgsComplete(call.name, call.args)) {
          if (call.timer) clearTimeout(call.timer);
          call.timer = null;
          fulfillRemoteCall(id);
          return;
        }
        // Otherwise keep a settle timer as a fallback in case an argument never
        // arrives (fulfillRemoteCall rejects the call if it's still incomplete).
        if (call.timer) clearTimeout(call.timer);
        call.timer = setTimeout(
          () => fulfillRemoteCall(id),
          REMOTE_TOOL_SETTLE_MS,
        );
        return;
      }

      if (event.generation_complete) {
        // A tool call happened this turn: this completion ends the pre-answer
        // tool-call pass, NOT the turn — the real answer streams after the tool
        // result comes back. Keep the "Thinking…" row up and KEEP the text
        // streamed so far (do NOT discard it: shifu sometimes puts real answer
        // content before a tool call, so dropping it loses part of the reply).
        // The turn is only committed on the final completion below. Handles
        // multiple tool passes: each just clears the flag and waits.
        if (toolCallPendingRef.current) {
          toolCallPendingRef.current = false;
          // We're betting a further completion follows this tool pass. Arm a
          // watchdog so a missing final completion (a stream that stops after
          // this one) can't leave the loader stuck forever. The next stream
          // event cancels it (see the top of this handler); if none arrives it
          // commits whatever streamed and clears the loader.
          if (loaderWatchdogRef.current)
            clearTimeout(loaderWatchdogRef.current);
          loaderWatchdogRef.current = setTimeout(() => {
            loaderWatchdogRef.current = null;
            const pendingText = turnTextRef.current;
            turnTextRef.current = '';
            if (pendingText) {
              setMessages(prev => [
                ...prev,
                {
                  id: `msg-rain-watchdog-${Date.now()}`,
                  role: 'assistant',
                  content: pendingText,
                },
              ]);
            }
            setStreamingContent('');
            setIsLoading(false);
          }, LOADER_WATCHDOG_MS);
          return;
        }

        const finalText = turnTextRef.current;
        turnTextRef.current = '';
        if (finalText) {
          setMessages(prev => [
            ...prev,
            {
              id: `msg-rain-${event.generation_complete?.id ?? Date.now()}`,
              role: 'assistant',
              content: finalText,
            },
          ]);
        }
        setStreamingContent('');
        setIsLoading(false);
        return;
      }

      if (event.generation_failed) {
        turnTextRef.current = '';
        toolCallPendingRef.current = false;
        setMessages(prev => [
          ...prev,
          {
            id: generateId(),
            role: 'assistant',
            content: t('integrations.assistant.panel.error'),
          },
        ]);
        setStreamingContent('');
        setIsLoading(false);
        return;
      }

      if (event.follow_up?.suggestion) {
        setQuickActions(prev => {
          if (prev.length >= MAX_QUICK_ACTIONS) return prev;
          if (prev.includes(event.follow_up!.suggestion)) return prev;
          return [...prev, event.follow_up!.suggestion];
        });
        setShowQuickActions(true);
      }
    },
    [t, fulfillRemoteCall],
  );

  const streamUrl = useMemo(
    () => (threadId ? `${agentBase}/threads/${threadId}/stream` : null),
    [agentBase, threadId],
  );

  useStream<IRainEvent>(
    streamUrl
      ? {
          url: streamUrl,
          protocol: EStreamProtocol.SSE,
          auth: {
            getToken: () => getAccessTokenSilently(),
            e2eTestMode: isE2ETestMode(),
          },
          observability: {
            module: 'integrations',
            operation: 'assistant_thread_stream',
          },
          onMessage: handleRainEvent,
        }
      : null,
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!threadIdRef.current) {
        console.warn('Rain thread not initialized yet');
        return;
      }

      setShowQuickActions(false);
      setQuickActions([]);
      turnTextRef.current = '';
      toolCallPendingRef.current = false;

      const firstTurnDiagnosis = hasSentRef.current
        ? undefined
        : diagnosisTurnRef.current;
      hasSentRef.current = true;

      const userMessage: IAssistantMessage = {
        id: generateId(),
        role: 'user',
        content: text,
      };

      setMessages(prev => {
        const opening =
          firstTurnDiagnosis && prev.length === 1
            ? [{ ...prev[0], content: firstTurnDiagnosis.greeting }]
            : prev;
        return [...opening, userMessage];
      });
      setIsLoading(true);
      setStreamingContent('');

      try {
        await rainSendMessage(
          agentBase,
          RAIN_AGENT_ID,
          threadIdRef.current,
          firstTurnDiagnosis
            ? buildDiagnosisContext(firstTurnDiagnosis.sections, text)
            : text,
        );
      } catch (error) {
        console.warn('Failed to send message to Rain:', error);
        const errorMessage: IAssistantMessage = {
          id: generateId(),
          role: 'assistant',
          content: t('integrations.assistant.panel.error'),
        };
        setMessages(prev => [...prev, errorMessage]);
        setStreamingContent('');
        setIsLoading(false);
      }
    },
    [agentBase, t],
  );

  const displayedMessages = useMemo(
    () =>
      diagnosisTurn && messages.length === 1
        ? [{ ...messages[0], content: diagnosisTurn.greeting }]
        : messages,
    [diagnosisTurn, messages],
  );

  return {
    messages: displayedMessages,
    sendMessage,
    isLoading,
    streamingContent,
    quickActions: showQuickActions ? quickActions : [],
  };
}
